/**
 * Motor de reconciliação — puro, sem rede e sem SQLite (ver docs/features/SYNC.md, ADR 0030).
 *
 * Reduz um histórico de mensagens ao estado atual por last-write-wins POR CAMPO. A propriedade
 * que importa: o resultado NÃO depende da ordem em que as mensagens chegam — é o que garante que
 * dois aparelhos convergem para a mesma base. Toda a lógica sensível ("saldo errado") mora aqui e
 * é testável no Jest sem emulador.
 */

import { compare } from './hlc';
import { type Message, TOMBSTONE_COLUMN, cellKey, messageKey, rowKey } from './messages';

/** Remove mensagens exatamente idênticas (mesma tupla), preservando a 1ª ocorrência. */
export function dedupe(messages: Message[]): Message[] {
    const seen = new Set<string>();
    const out: Message[] = [];
    for (const m of messages) {
        const k = messageKey(m);
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(m);
    }
    return out;
}

/** Une dois conjuntos de mensagens (dedup exato). Comutativo por construção. */
export function merge(a: Message[], b: Message[]): Message[] {
    return dedupe([...a, ...b]);
}

/** Para cada célula (tabela, linha, coluna), a mensagem de maior HLC — o vencedor do LWW. */
export function pickWinners(messages: Message[]): Map<string, Message> {
    const winners = new Map<string, Message>();
    for (const m of messages) {
        const k = cellKey(m);
        const cur = winners.get(k);
        if (!cur || compare(m.timestamp, cur.timestamp) > 0) {
            winners.set(k, m);
        }
    }
    return winners;
}

export type MaterializedRow = Record<string, unknown>;
export type MaterializedTable = Record<string, MaterializedRow>;
export type MaterializedState = Record<string, MaterializedTable>;

/**
 * Reduz o histórico ao estado atual: aplica o vencedor de cada célula e OMITE linhas cujo
 * tombstone vencedor está verdadeiro. A ordem de entrada é irrelevante (LWW por HLC) — daí a
 * convergência. Uma edição de dado NÃO ressuscita linha apagada: só uma mensagem de tombstone
 * mais nova com valor falso (restauração) tira a linha do estado apagado.
 */
export function materialize(messages: Message[]): MaterializedState {
    const winners = pickWinners(messages);
    const tombstoned = new Set<string>();
    const state: MaterializedState = {};

    // 1ª passada: linhas cujo estado de tombstone vencedor é "apagada".
    for (const m of winners.values()) {
        if (m.column === TOMBSTONE_COLUMN && Boolean(m.value)) {
            tombstoned.add(rowKey(m));
        }
    }

    // 2ª passada: monta as linhas vivas a partir das colunas de dado.
    for (const m of winners.values()) {
        if (m.column === TOMBSTONE_COLUMN) continue;
        if (tombstoned.has(rowKey(m))) continue;
        const table = (state[m.table] ??= {});
        const row = (table[m.row] ??= {});
        row[m.column] = m.value;
    }

    return state;
}
